(() => {
  if (window.SCHomeAudience) return;

  const audiences = new Set(['homme', 'femme']);
  const roots = new WeakMap();
  const carousels = new Map();
  const storageKey = 'sc-home-audience';
  let currentAudience = null;

  function storedAudience() {
    try {
      const value = window.sessionStorage.getItem(storageKey);
      return audiences.has(value) ? value : null;
    } catch {
      return null;
    }
  }

  function rememberAudience(audience) {
    try { window.sessionStorage.setItem(storageKey, audience); } catch {}
  }

  function panels(root) {
    return Array.from(root.querySelectorAll('[data-sc-audience-panel]')).filter(panel => audiences.has(panel.dataset.scAudiencePanel));
  }

  function applyAudience(root, audience) {
    const list = panels(root);
    if (!list.some(panel => panel.dataset.scAudiencePanel === audience)) return false;
    for (const panel of list) panel.hidden = panel.dataset.scAudiencePanel !== audience;
    root.dataset.scAudienceReady = 'true';
    root.dataset.activeAudience = audience;
    roots.set(root, audience);
    const label = audience === 'homme' ? root.dataset.labelHomme : root.dataset.labelFemme;
    if (typeof label === 'string') {
      for (const status of root.querySelectorAll('[data-sc-audience-status]')) {
        if (status.textContent !== label) status.textContent = label;
      }
    }
    return true;
  }

  function updateChoices(audience) {
    for (const choice of document.querySelectorAll('a[data-sc-audience-choice]')) {
      const selected = choice.dataset.scAudienceChoice === audience;
      choice.setAttribute('aria-current', String(selected));
      choice.classList.toggle('is-active', selected);
    }
  }

  function updateCarousel(record) {
    const target = record.target;
    for (const button of record.buttons) {
      button.disabled = button.dataset.scCarouselDirection === 'previous' ? target.scrollLeft <= 1 : target.scrollLeft + target.clientWidth >= target.scrollWidth - 1;
    }
  }

  function disconnectCarousel(record) {
    record.target.removeEventListener('scroll', record.onScroll);
    for (const control of record.controls) control.hidden = true;
    carousels.delete(record.target);
  }

  function connectCarousels() {
    const groups = new Map();
    for (const control of document.querySelectorAll('[data-sc-carousel-controls]')) {
      let found = false;
      for (const button of control.querySelectorAll('button[data-sc-carousel-target][data-sc-carousel-direction]')) {
        const target = document.getElementById(button.dataset.scCarouselTarget);
        if (!target || !['previous', 'next'].includes(button.dataset.scCarouselDirection)) {
          button.disabled = true;
          continue;
        }
        found = true;
        if (!groups.has(target)) groups.set(target, { buttons: new Set(), controls: new Set() });
        groups.get(target).buttons.add(button);
        groups.get(target).controls.add(control);
      }
      control.hidden = !found;
    }
    for (const [target, record] of carousels) {
      if (!groups.has(target)) disconnectCarousel(record);
    }
    for (const [target, group] of groups) {
      let record = carousels.get(target);
      if (!record) {
        record = { target };
        record.onScroll = () => updateCarousel(record);
        target.addEventListener('scroll', record.onScroll, { passive: true });
        carousels.set(target, record);
      }
      record.buttons = group.buttons;
      record.controls = group.controls;
      for (const control of record.controls) control.hidden = false;
      updateCarousel(record);
    }
  }

  function scrollCarousel(event) {
    if (event.defaultPrevented) return false;
    const button = event.target?.closest?.('button[data-sc-carousel-target][data-sc-carousel-direction]');
    if (!button) return false;
    const target = document.getElementById(button.dataset.scCarouselTarget);
    const direction = button.dataset.scCarouselDirection;
    if (!target || !carousels.has(target) || !['previous', 'next'].includes(direction)) return false;
    if (button.disabled) return true;
    event.preventDefault();
    const first = target.querySelector('li');
    const width = first?.getBoundingClientRect().width || 0;
    const style = window.getComputedStyle?.(target);
    const measuredGap = Number.parseFloat(style?.columnGap || style?.gap);
    const gap = Number.isFinite(measuredGap) && measuredGap >= 0 ? measuredGap : 16;
    const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
    target.scrollBy({ left: (direction === 'previous' ? -1 : 1) * (width + gap), behavior });
    return true;
  }

  function connect() {
    connectCarousels();
    const available = Array.from(document.querySelectorAll('[data-sc-home-discovery]')).filter(root => panels(root).length);
    if (!available.length) return;
    if (!currentAudience) {
      const fallback = available[0].dataset.defaultAudience;
      currentAudience = storedAudience() || (audiences.has(fallback) ? fallback : 'homme');
    }
    let applied = false;
    for (const root of available) applied = applyAudience(root, currentAudience) || applied;
    if (applied) {
      updateChoices(currentAudience);
      for (const record of carousels.values()) updateCarousel(record);
    }
  }

  function choose(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0)) return;
    const choice = event.target?.closest?.('a[data-sc-audience-choice]');
    const audience = choice?.dataset.scAudienceChoice;
    if (!audiences.has(audience)) return;
    const available = Array.from(document.querySelectorAll('[data-sc-home-discovery]')).filter(root => roots.has(root) && panels(root).some(panel => panel.dataset.scAudiencePanel === audience));
    if (!available.length) return;
    event.preventDefault();
    currentAudience = audience;
    for (const root of available) applyAudience(root, audience);
    updateChoices(audience);
    for (const record of carousels.values()) updateCarousel(record);
    rememberAudience(audience);
    if (choice.dataset.scAudienceScroll === 'true') {
      const localRoot = choice.closest('[data-sc-home-discovery]');
      const target = available.includes(localRoot) ? localRoot : available[0];
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
      target.scrollIntoView({ behavior, block: 'start' });
    }
  }

  function unload(event) {
    const section = event.target;
    const available = Array.from(section?.querySelectorAll?.('[data-sc-home-discovery]') || []);
    if (section?.matches?.('[data-sc-home-discovery]')) available.push(section);
    for (const root of available) {
      roots.delete(root);
      delete root.dataset.scAudienceReady;
      delete root.dataset.activeAudience;
      for (const panel of panels(root)) panel.hidden = false;
    }
    for (const record of carousels.values()) {
      if (section === record.target || section?.contains?.(record.target)) disconnectCarousel(record);
    }
  }

  function initialize() {
    connect();
    document.addEventListener('click', event => {
      if (!scrollCarousel(event)) choose(event);
    });
    document.addEventListener('shopify:section:load', connect);
    document.addEventListener('shopify:section:unload', unload);
    window.addEventListener('resize', connectCarousels, { passive: true });
  }

  window.SCHomeAudience = Object.freeze({ refresh: connect });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
